import { useEffect, useMemo, useState } from 'react'
import { loadGoogleMapsScript } from '../googleMapsLoader'

interface ResolvedPlace {
  placeId: string
  name: string
  formattedAddress: string | null
  location: { lat: number; lng: number } | null
  viewport: google.maps.LatLngBoundsLiteral | null
}

const placeCache = new Map<string, ResolvedPlace>()
const inflightRequests = new Map<string, Promise<ResolvedPlace | null>>()

function geocodeByPlaceId(placeId: string): Promise<ResolvedPlace | null> {
  const cached = placeCache.get(placeId)
  if (cached) {
    return Promise.resolve(cached)
  }

  const inflight = inflightRequests.get(placeId)
  if (inflight) {
    return inflight
  }

  const geocodePromise = new Promise<ResolvedPlace | null>((resolve) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive guard, google maps may not be loaded
    if (!window.google?.maps) {
      resolve(null)
      return
    }

    const geocoder = new google.maps.Geocoder()
    void geocoder.geocode({ placeId }, (results, status) => {
      if ((status as google.maps.GeocoderStatus) !== google.maps.GeocoderStatus.OK || !results?.[0]) {
        resolve(null)
        return
      }

      const result = results[0]
      const location = result.geometry.location
      const viewport = result.geometry.viewport
      const formattedAddress = result.formatted_address || null

      const resolved: ResolvedPlace = {
        placeId,
        name: formattedAddress ?? placeId,
        formattedAddress,
        location: { lat: location.lat(), lng: location.lng() },
        viewport: viewport.toJSON(),
      }

      placeCache.set(placeId, resolved)
      resolve(resolved)
    })
  }).finally(() => {
    inflightRequests.delete(placeId)
  })

  inflightRequests.set(placeId, geocodePromise)
  return geocodePromise
}

/** Resolve a Google Place ID for non-rendering consumers such as printing. */
export async function resolvePlaceAddress(placeId: string): Promise<string | null> {
  const normalizedPlaceId = placeId.trim()
  if (!normalizedPlaceId) return null

  const cached = placeCache.get(normalizedPlaceId)
  if (cached) return cached.formattedAddress

  await loadGoogleMapsScript()
  const resolved = await geocodeByPlaceId(normalizedPlaceId)
  return resolved?.formattedAddress ?? null
}

export function useResolvedPlaces(placeIds: string[]) {
  const [resolvedById, setResolvedById] = useState<Record<string, ResolvedPlace | undefined>>({})
  const normalizedPlaceIds = useMemo(
    () => placeIds.map((id) => id.trim()).filter((id) => id.length > 0),
    [placeIds]
  )
  const placeIdsKey = useMemo(
    () => JSON.stringify(normalizedPlaceIds),
    [normalizedPlaceIds]
  )
  const uniquePlaceIds = useMemo(
    () => Array.from(new Set(normalizedPlaceIds)),
    [normalizedPlaceIds]
  )

  useEffect(() => {
    let isCancelled = false

    if (uniquePlaceIds.length === 0) {
      setResolvedById({})
      return
    }

    const syncFromCache = () => {
      if (isCancelled) return

      const next: Record<string, ResolvedPlace> = {}
      uniquePlaceIds.forEach((placeId) => {
        const cached = placeCache.get(placeId)
        if (cached) next[placeId] = cached
      })
      setResolvedById(next)
    }

    syncFromCache()

    const unresolved = uniquePlaceIds.filter((placeId) => !placeCache.has(placeId))
    if (unresolved.length === 0) return

    void loadGoogleMapsScript()
      .then(async () => {
        await Promise.all(unresolved.map((placeId) => geocodeByPlaceId(placeId)))
        syncFromCache()
      })
      .catch(() => {
        // Consumers retain their existing fallback when Maps is unavailable.
      })

    return () => {
      isCancelled = true
    }
  }, [placeIdsKey, uniquePlaceIds])

  return resolvedById
}
